import { Component, Input, OnInit } from '@angular/core';
import { AiFlowService, AiFlowSettings, AiFlowUpdate } from '../../core/services/core.ai-flow.service';

@Component({
    selector: 'ai-flow-settings',
    template: require('./ai-flow-settings.component.html'),
})
export class AiFlowSettingsComponent implements OnInit {

    @Input() projectId: number;
    @Input() projectName: string;

    settings: AiFlowSettings | null = null;
    loading = false;
    saving = false;
    error: string | null = null;

    aiEnabledValue: boolean = false;

    // Current edit values for the three LLM fields (only populated when editable)
    editValues: { llm_provider: string; llm_model: string; llm_key: string } = {
        llm_provider: '',
        llm_model: '',
        llm_key: '',
    };

    // Tracks which inherited fields the user has clicked Override on
    overriding: { llm_provider: boolean; llm_model: boolean; llm_key: boolean } = {
        llm_provider: false,
        llm_model: false,
        llm_key: false,
    };

    choices = { 'Enabled': true, 'Disabled': false };
    providerChoices = { 'None': '', 'OpenAI': 'openai', 'Anthropic': 'anthropic', 'Google': 'google', 'Mistral AI': 'mistral', 'AWS Bedrock': 'bedrock' };

    showKey = false;

    constructor(private aiFlowService: AiFlowService) {}

    ngOnInit() {
        this.loadSettings();
    }

    loadSettings() {
        this.loading = true;
        this.error = null;
        this.aiFlowService.getAiConfig(this.projectId)
            .then(settings => {
                this.settings = settings;
                this.aiEnabledValue = settings.ai_enabled.value === true;
                this.editValues = {
                    llm_provider: settings.llm_provider.inherited ? '' : (settings.llm_provider.value as string || ''),
                    llm_model:    settings.llm_model.inherited    ? '' : (settings.llm_model.value    as string || ''),
                    llm_key:      settings.llm_key.inherited      ? '' : (settings.llm_key.value      as string || ''),
                };
                this.overriding = { llm_provider: false, llm_model: false, llm_key: false };
                this.showKey = false;
                this.loading = false;
            })
            .catch(err => {
                this.error = (err && err.error && err.error.message) || 'Failed to load AI settings';
                this.loading = false;
            });
    }

    isEditable(field: 'llm_provider' | 'llm_model' | 'llm_key'): boolean {
        return this.settings ? (!this.settings[field].inherited || this.overriding[field]) : false;
    }

    override(field: 'llm_provider' | 'llm_model' | 'llm_key') {
        this.overriding[field] = true;
        this.editValues[field] = '';
    }

    clearField(field: 'llm_provider' | 'llm_model' | 'llm_key') {
        const update: AiFlowUpdate = {};
        update[field] = '';
        this.saving = true;
        this.aiFlowService.postAiConfig(this.projectId, update)
            .then(() => {
                this.saving = false;
                this.loadSettings();
            })
            .catch(err => {
                this.error = (err && err.error && err.error.message) || 'Failed to clear setting';
                this.saving = false;
            });
    }

    save() {
        if (!this.settings) { return; }

        const changes: AiFlowUpdate = {};

        // Only include ai_enabled if it changed from the loaded value
        const loadedAiEnabled = this.settings.ai_enabled.value === true;
        if (this.aiEnabledValue !== loadedAiEnabled) {
            changes.ai_enabled = this.aiEnabledValue;
        }

        // Include LLM fields that the user has explicitly set a value for:
        // - overriding an inherited field: always include (user clicked Override and is writing a new value)
        // - own (non-inherited) field: only include if the edit value is non-empty (empty means unchanged/untouched)
        const llmFields: Array<'llm_provider' | 'llm_model' | 'llm_key'> = ['llm_provider', 'llm_model', 'llm_key'];
        for (const field of llmFields) {
            if (this.overriding[field] || (!this.settings[field].inherited && this.editValues[field] !== '')) {
                changes[field] = this.editValues[field];
            }
        }

        if (Object.keys(changes).length === 0) { return; }

        this.saving = true;
        this.aiFlowService.postAiConfig(this.projectId, changes)
            .then(() => {
                this.saving = false;
                this.loadSettings();
            })
            .catch(err => {
                this.error = (err && err.error && err.error.message) || 'Failed to save settings';
                this.saving = false;
            });
    }

    cancel() {
        this.loadSettings();
    }

    toggleShowKey() {
        this.showKey = !this.showKey;
    }
}
